import React from "react";
import styled from "styled-components";
import { CompassParagraph } from "../../components/common/styledComponents";
import { CountryWFlag } from "../../components/visuals/CountryWFlag";

import {
  ElementContainer,
  FlexibleContainer,
  RowContainer,
} from "../../components/containers/styledComponents";
import { formatNumber } from "../../utils/formattingUtils";
import { CompanySummary } from "../../models/CompanyprofileModels";
import { MESSAGES } from "../../messages";
import { ONE_BILLION, ONE_MILLION } from "../../constants";
import { KPIBox } from "../../components/visuals/KPIBox";
import allSharesIcon from "../../assets/svg/all_shares.svg";
import walletIcon from "../../assets/svg/wallet_green.svg";
import { KeyValueTable } from "../../components/tables/KeyValueTable";
import { DataEntry } from "../../models/stewardshipModels";

const CompanyDataItemContainer = styled(ElementContainer)<{
  isTable?: boolean;
}>`
  display: flex;
  min-width: ${({ isTable }): string => (isTable ? "550px" : "auto")};
  max-width: 100%;
  height: auto;
  flex-direction: column;
  padding: 40px 20px;
  overflow: hidden;
`;

export const CompanyData = (props: CompanySummary): JSX.Element => {
  const marketCap = props?.key_metrics?.market_cap || 0;
  const revenue = props.revenue * ONE_MILLION || 0; // Revenue from db is given in millions
  const enterpriseValue = props?.key_metrics?.ev || 0;
  const country = props?.country || "US";

  const CompanyCountry = (): JSX.Element => (
    <FlexibleContainer jc="flex-start" ml="-8px">
      <CountryWFlag countryIso2={country} />
    </FlexibleContainer>
  );

  const dataEntries: DataEntry[] = [
    { label: "Sector", value: props.sector },
    { label: "Symbol", value: props.symbol || MESSAGES.NOT_AVAILABLE },
    { label: "Country", value: <CompanyCountry /> },
    { label: "Employees", value: formatNumber(props.employee_count || 0) },

    { label: "Director", value: props.ceo || MESSAGES.NOT_AVAILABLE },
    {
      label: "Revenue",
      value: formatNumber(revenue, revenue >= ONE_BILLION ? "B" : "M"),
    },
    {
      label: "Market Cap",
      value: formatNumber(marketCap, marketCap >= ONE_BILLION ? "B" : "M"),
    },
    {
      label: "Share Price",
      value: formatNumber(props.price || 0),
    },
    {
      label: "Shares Outstanding",
      value: formatNumber(props.shares_outstanding || 0, "M"),
    },
    {
      label: "Enterprise Value",
      value: formatNumber(
        enterpriseValue,
        enterpriseValue >= ONE_BILLION ? "B" : "M"
      ),
    },
    {
      label: "Debt to Equity",
      value: formatNumber(props.key_metrics?.d_e || 0),
      isPercentage: true,
    },
    {
      label: "P/E Ratio",
      value: formatNumber(props.key_metrics?.p_e || 0),
    },
    {
      label: "Return on Equity",
      value: formatNumber(props.key_metrics?.roe || 0),
      isPercentage: true,
    },
  ];

  return (
    <FlexibleContainer jc="flex-start" ai="flex-start">
      <CompanyDataItemContainer isTable={true}>
        <CompassParagraph fontSize={14} bold={true}>
          Corporate Data
        </CompassParagraph>
        <KeyValueTable
          columns={[{ title: "Key Metrics" }, { title: "Value" }]}
          data={dataEntries.slice(0, 5)}
        />
      </CompanyDataItemContainer>
      <CompanyDataItemContainer ml={100}>
        <KPIBox
          icon={walletIcon}
          title={"Valution Overview"}
          kpis={dataEntries.slice(5, 9) as DataEntry<number>[]}
        />
        <RowContainer />
        <KPIBox
          icon={allSharesIcon}
          title={"Financial Ratios"}
          kpis={dataEntries.slice(9, 15) as DataEntry<number>[]}
        />
      </CompanyDataItemContainer>
    </FlexibleContainer>
  );
};
